import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Paper from "@mui/material/Paper";
import Skeleton from "@mui/material/Skeleton";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import { getExpressDocuments } from "../../services/expressDocument";
import { ExpressDocumentListRow } from "../../types/express-document.type";
import { formatNumber } from "../../utils";
import { formatBuddhistShortDate } from "../../utils/date";
import SpecialOrderPagination from "../SpecialOrder/Pagination";
import StatusChip from "./StatusChip";
import { ExpressDocTypeConfig, documentDetailPath } from "./constants";
import { gridHeadSx } from "./gridStyles";
import { DocumentFilterParams } from "./documentListFilter";

interface DocumentTableProps {
  config: ExpressDocTypeConfig;
  /** The list's filters, search already debounced; a new object only when a filter changes. */
  params: DocumentFilterParams;
}

const COLUMN_COUNT = 7;
const DEFAULT_LIMIT = 10;
const SKELETON_ROWS = 5;

const headerCellSx = { borderRight: 1, borderColor: "draft.main", whiteSpace: "nowrap" };
const rowSx = { cursor: "pointer", "& td": { padding: "8px 12px" } };

function DocumentRow({
  config,
  row,
}: Readonly<{ config: ExpressDocTypeConfig; row: ExpressDocumentListRow }>) {
  const navigate = useNavigate();
  const handleClick = () => navigate(documentDetailPath(config, row.uuid));

  return (
    <TableRow hover onClick={handleClick} sx={rowSx}>
      <TableCell>
        <Typography variant="body2" fontWeight={600} color="primary">
          {row.doc_no}
        </Typography>
      </TableCell>
      <TableCell>{formatBuddhistShortDate(row.doc_date)}</TableCell>
      <TableCell>{row.party_code ?? "-"}</TableCell>
      <TableCell>{row.party_name ?? "-"}</TableCell>
      <TableCell>{row.remark ?? "-"}</TableCell>
      <TableCell align="right">
        <Typography variant="body2" fontWeight={600}>
          {formatNumber(row[config.listAmountField])}
        </Typography>
      </TableCell>
      <TableCell align="center">
        <StatusChip status={row.status} />
      </TableCell>
    </TableRow>
  );
}

/** One type's documents, newest first as the server sorts them, a page at a time. */
export default function DocumentTable({ config, params }: Readonly<DocumentTableProps>) {
  const [page, setPage] = useState<number>(1);
  const [limit, setLimit] = useState<number>(DEFAULT_LIMIT);
  const [rows, setRows] = useState<ExpressDocumentListRow[]>([]);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);

  const fetchDocuments = useCallback(async () => {
    setLoading(true);
    try {
      const {
        data: { data },
      } = await getExpressDocuments({ ...params, page, limit });
      setRows(data?.rows ?? []);
      setTotalCount(data?.count ?? 0);
    } catch {
      // The interceptor already shows why the list could not be read.
      setRows([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [page, limit, params]);

  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  // Any filter change starts again from the first page.
  useEffect(() => {
    setPage(1);
  }, [params]);

  const isEmpty = !loading && rows.length === 0;
  const skeletonRows = loading ? Array.from({ length: SKELETON_ROWS }, (_, index) => index) : [];
  const amountLabel = config.listAmountField === "received_amount" ? "ยอดชำระจริง" : "จำนวนเงินรวม";
  const skeletonCells = Array.from({ length: COLUMN_COUNT }, (_, index) => index);

  return (
    <Paper sx={{ p: 0, overflow: "hidden" }}>
      <TableContainer>
        <Table>
          <TableHead sx={gridHeadSx}>
            <TableRow>
              <TableCell sx={headerCellSx}>{config.docNoLabel}</TableCell>
              <TableCell sx={headerCellSx}>{config.docDateLabel}</TableCell>
              <TableCell sx={headerCellSx}>{config.partyLabel}</TableCell>
              <TableCell sx={headerCellSx}>ชื่อ</TableCell>
              <TableCell sx={headerCellSx}>{config.remarkLabel}</TableCell>
              <TableCell sx={headerCellSx} align="right">
                {amountLabel}
              </TableCell>
              <TableCell align="center">สถานะ</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {skeletonRows.map((rowIndex) => (
              <TableRow key={`skeleton-${rowIndex}`}>
                {skeletonCells.map((cellIndex) => (
                  <TableCell key={cellIndex}>
                    <Skeleton variant="text" />
                  </TableCell>
                ))}
              </TableRow>
            ))}
            {isEmpty && (
              <TableRow>
                <TableCell colSpan={COLUMN_COUNT} align="center" sx={{ py: 5 }}>
                  <Typography color="text.secondary" fontWeight={500}>
                    ไม่พบเอกสารตามเงื่อนไข
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {!loading && rows.map((row) => <DocumentRow key={row.uuid} config={config} row={row} />)}
          </TableBody>
        </Table>
      </TableContainer>
      <Stack px={2} pt={1.5}>
        <Typography variant="body2" color="text.secondary">
          ทั้งหมด {totalCount} รายการ
        </Typography>
      </Stack>
      <SpecialOrderPagination
        totalCount={totalCount}
        page={page}
        setPage={setPage}
        limit={limit}
        setLimit={setLimit}
      />
    </Paper>
  );
}
